import { useEffect, useMemo, useState } from 'react';
import { History } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  RESULTATS_ECF,
  useSaisirResultatsEcf,
  type LigneEcf,
  type ResultatEcf,
} from '../../lib/useEcf';
import { useDemanderModification } from '../../lib/useModificationsSensibles';
import {
  AvisValidation,
  ChampMotif,
  DemandeEnCours,
  motifValide,
  useValidationDirecte,
} from '../modifications-sensibles/ElementsDemande';
import { jourLisible } from './format';

/**
 * Saisie des résultats d'ECF, en grille : une ligne par apprenant d'une même
 * évaluation (bloc, date, promotion), ou une seule ligne ouverte depuis le
 * tableau.
 *
 * Un résultat saisi est lu par l'apprenant : le corriger est une modification
 * sensible (TF-437). Ouvert seul, il se corrige par une demande à la
 * direction, motivée — la direction applique elle-même la sienne ; en grille,
 * il reste en lecture. L'ancien résultat reste tracé, c'est l'API qui le garde.
 */

interface Saisie {
  resultat: ResultatEcf | null;
  observation: string;
}

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy';
const CLASSE_LIBELLE = 'mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy';

function saisieInitiale(ligne: LigneEcf): Saisie {
  return { resultat: ligne.resultat, observation: ligne.observation ?? '' };
}

function aChange(ligne: LigneEcf, saisie: Saisie): boolean {
  if (saisie.resultat === null) return false;
  return (
    saisie.resultat !== ligne.resultat || saisie.observation.trim() !== (ligne.observation ?? '')
  );
}

function LigneSaisie({
  ligne,
  saisie,
  onChange,
  seule,
  correction,
}: {
  ligne: LigneEcf;
  saisie: Saisie;
  onChange: (saisie: Saisie) => void;
  seule: boolean;
  /** Ligne déjà saisie, ouverte seule : sa correction est une demande. */
  correction: boolean;
}): JSX.Element {
  const refus = correction ? ligne.refusCorrection : ligne.refusSaisie;
  return (
    <div className="space-y-3 px-4 py-4 sm:px-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-codialis-navy">{ligne.apprenant}</div>
          {/* En grille, bloc et date sont ceux de l'en-tête. */}
          {seule && (
            <div className="text-[11px] text-codialis-gray">
              {ligne.bloc} · {jourLisible(ligne.datePrevue)}
            </div>
          )}
        </div>
        {ligne.resultatLibelle && (
          <Pastille texte={`Saisi : ${ligne.resultatLibelle}`} ton={ligne.resultatTon ?? 'neutral'} />
        )}
      </div>

      {correction && ligne.demandeEnAttente ? (
        <DemandeEnCours demande={ligne.demandeEnAttente} />
      ) : refus ? (
        <p className="text-xs text-amber-700">{refus}</p>
      ) : (
        <>
          <div
            role="radiogroup"
            aria-label={`Résultat de ${ligne.apprenant}`}
            className="flex flex-wrap gap-1.5"
          >
            {RESULTATS_ECF.map((option) => {
              const choisi = saisie.resultat === option.valeur;
              return (
                <button
                  key={option.valeur}
                  type="button"
                  role="radio"
                  aria-checked={choisi}
                  onClick={() => onChange({ ...saisie, resultat: option.valeur })}
                  className={`min-h-[40px] rounded-lg px-3 text-xs font-semibold transition ${
                    choisi
                      ? 'bg-codialis-navy text-white'
                      : 'border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                  }`}
                >
                  {option.libelle}
                </button>
              );
            })}
          </div>
          <label className="block">
            <span className={CLASSE_LIBELLE}>Observation (lue par l’apprenant)</span>
            <textarea
              value={saisie.observation}
              onChange={(evenement) => onChange({ ...saisie, observation: evenement.target.value })}
              maxLength={2000}
              rows={seule ? 3 : 2}
              className={`${CLASSE_CHAMP} py-2`}
            />
          </label>
        </>
      )}

      {seule && ligne.corrections.length > 0 && (
        <div className="rounded-xl bg-codialis-sand p-3">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            <History className="h-3.5 w-3.5" aria-hidden="true" />
            Corrections
          </div>
          <ul className="space-y-1.5 text-xs text-codialis-gray">
            {ligne.corrections.map((correctionPassee) => (
              <li key={correctionPassee.le}>
                {new Date(correctionPassee.le).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' })}
                {correctionPassee.par ? ` · ${correctionPassee.par}` : ''} : {correctionPassee.ancien} →{' '}
                {correctionPassee.nouveau} — {correctionPassee.motif}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function ModaleSaisieEcf({
  ouverte,
  onFermer,
  lignes,
  titre = 'Saisir les résultats ECF',
}: {
  ouverte: boolean;
  onFermer: () => void;
  /** Lignes d'une même évaluation, ou une seule ligne. */
  lignes: LigneEcf[];
  titre?: string;
}): JSX.Element | null {
  const notifier = useToast();
  const saisir = useSaisirResultatsEcf();
  const demander = useDemanderModification();
  const directe = useValidationDirecte();
  const [saisies, setSaisies] = useState<Record<string, Saisie>>({});
  const [motif, setMotif] = useState('');

  const cle = lignes.map((ligne) => ligne.id).join('|');
  useEffect(() => {
    if (!ouverte) return;
    setSaisies(Object.fromEntries(lignes.map((ligne) => [ligne.id, saisieInitiale(ligne)])));
    setMotif('');
    saisir.reset();
    demander.reset();
    // Réinitialisée à chaque ouverture, pour le jeu de lignes ouvert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte, cle]);

  const seule = lignes.length === 1;
  // Une seule ligne déjà saisie : la fenêtre corrige, par une demande.
  const enCorrection = seule && lignes[0]!.resultat !== null;
  const ligneCorrigee = enCorrection ? lignes[0]! : null;
  const correctionOuverte = ligneCorrigee !== null && ligneCorrigee.refusCorrection === null;

  const aEnvoyer = useMemo(
    () =>
      enCorrection
        ? []
        : lignes.filter(
            (ligne) => ligne.resultat === null && saisies[ligne.id] && aChange(ligne, saisies[ligne.id]!),
          ),
    [lignes, saisies, enCorrection],
  );
  const correctionPrete =
    ligneCorrigee !== null &&
    correctionOuverte &&
    saisies[ligneCorrigee.id] !== undefined &&
    aChange(ligneCorrigee, saisies[ligneCorrigee.id]!) &&
    motifValide(motif);

  function enregistrer(): void {
    saisir.mutate(
      aEnvoyer.map((ligne) => {
        const saisie = saisies[ligne.id]!;
        return {
          evaluationId: ligne.id,
          resultat: saisie.resultat!,
          observation: saisie.observation.trim() || null,
        };
      }),
      {
        onSuccess: ({ message }) => {
          notifier(message);
          onFermer();
        },
      },
    );
  }

  function demanderCorrection(): void {
    if (!ligneCorrigee) return;
    const saisie = saisies[ligneCorrigee.id]!;
    demander.mutate(
      {
        type: 'RESULTAT_ECF',
        evaluationId: ligneCorrigee.id,
        resultat: saisie.resultat!,
        observation: saisie.observation.trim() || null,
        motif: motif.trim(),
      },
      {
        onSuccess: ({ message }) => {
          notifier(message);
          onFermer();
        },
      },
    );
  }

  const erreur = (enCorrection ? demander.error : saisir.error) ?? null;
  return (
    <Modale
      titre={enCorrection ? (directe ? 'Corriger le résultat ECF' : 'Demander une correction') : titre}
      sousTitre={
        seule
          ? `${lignes[0]?.promotion ?? ''}`
          : `${lignes[0]?.bloc ?? ''} · ${lignes[0] ? jourLisible(lignes[0].datePrevue) : ''} · ${lignes.length} apprenants`
      }
      ouverte={ouverte}
      onFermer={onFermer}
    >
      <div className="divide-y divide-gray-100">
        {lignes.map((ligne) => (
          <LigneSaisie
            key={ligne.id}
            ligne={ligne}
            seule={seule}
            correction={enCorrection}
            saisie={saisies[ligne.id] ?? saisieInitiale(ligne)}
            onChange={(saisie) => setSaisies((avant) => ({ ...avant, [ligne.id]: saisie }))}
          />
        ))}
      </div>
      <div className="space-y-3 border-t border-gray-100 p-4 sm:px-6">
        {correctionOuverte && (
          <>
            <ChampMotif
              valeur={motif}
              onChange={setMotif}
              directe={directe}
              placeholder="Grille mal reportée, erreur de saisie…"
            />
            <AvisValidation directe={directe} />
          </>
        )}
        {erreur instanceof ErreurApi && <MessageErreur message={erreur.message} />}
        <div className="flex flex-wrap justify-end gap-2">
          <BoutonSecondaire onClick={onFermer}>{correctionOuverte || !enCorrection ? 'Annuler' : 'Fermer'}</BoutonSecondaire>
          {enCorrection ? (
            correctionOuverte && (
              <BoutonPrincipal onClick={demanderCorrection} disabled={!correctionPrete || demander.isPending}>
                {demander.isPending ? 'Envoi…' : directe ? 'Appliquer la correction' : 'Envoyer la demande'}
              </BoutonPrincipal>
            )
          ) : (
            <BoutonPrincipal onClick={enregistrer} disabled={aEnvoyer.length === 0 || saisir.isPending}>
              {saisir.isPending
                ? 'Enregistrement…'
                : aEnvoyer.length > 1
                  ? `Enregistrer ${aEnvoyer.length} résultats`
                  : 'Enregistrer'}
            </BoutonPrincipal>
          )}
        </div>
      </div>
    </Modale>
  );
}
